
import React, {
    createContext,
    useCallback,
    useContext,
    Suspense,
    useMemo,
    useRef,
    useState,
} from 'react';

import { createPortal } from 'react-dom';

const AdminProduct3DPreview = React.lazy(() => import('./AdminProduct3DPreview'));

const AdminProduct3DViewerContext = createContext(null);

const emptyViewer = {
    isOpen: false,
    product: null,
    products: [],
    categories: [],
    mode: 'viewer',
    blankDimensions: null,
    showBlankFrame: true,
    viewerTitle: '',
    onClose: null,
    onProductUpdated: null,
    onSelectProduct: null,
};

export const AdminProduct3DViewerProvider = ({ children }) => {
    const [viewer, setViewer] = useState(emptyViewer);
    const viewerRef = useRef(viewer);
    viewerRef.current = viewer;

    const openProduct3D = useCallback((options = {}) => {
        if (!options.product?._id) return false;

        setViewer({
            ...emptyViewer,
            ...options,
            isOpen: true,
            product: options.product,
            products: Array.isArray(options.products) && options.products.length
                ? options.products
                : [options.product],
            categories: Array.isArray(options.categories) ? options.categories : [],
            mode: options.mode || 'viewer',
            showBlankFrame: options.showBlankFrame !== false,
        });

        return true;
    }, []);

    const closeProduct3D = useCallback(({ notify = true } = {}) => {
        const current = viewerRef.current;

        setViewer((previous) => ({
            ...previous,
            isOpen: false,
        }));

        if (notify && typeof current.onClose === 'function') {
            current.onClose();
        }
    }, []);

    const selectProduct = useCallback((product) => {
        if (!product?._id) return;

        setViewer((previous) => ({
            ...previous,
            product,
            blankDimensions: product._catalogReviewDimensions
                || previous.blankDimensions
                || null,
        }));

        const current = viewerRef.current;
        if (typeof current.onSelectProduct === 'function') {
            current.onSelectProduct(product);
        }
    }, []);

    const handleProductUpdated = useCallback((updatedProduct) => {
        if (!updatedProduct?._id) return;

        setViewer((previous) => ({
            ...previous,
            product: String(previous.product?._id) === String(updatedProduct._id)
                ? { ...previous.product, ...updatedProduct }
                : previous.product,
            products: previous.products.map((product) => (
                String(product?._id) === String(updatedProduct._id)
                    ? { ...product, ...updatedProduct }
                    : product
            )),
        }));

        const current = viewerRef.current;
        if (typeof current.onProductUpdated === 'function') {
            current.onProductUpdated(updatedProduct);
        }
    }, []);

    const value = useMemo(() => ({
        viewer,
        openProduct3D,
        closeProduct3D,
    }), [viewer, openProduct3D, closeProduct3D]);

    return (
        <AdminProduct3DViewerContext.Provider value={value}>
            {children}

            {viewer.isOpen && typeof document !== 'undefined' ? createPortal((
                <Suspense fallback={(
                    <div className="adminProduct3DPreview__lazyShell">
                        <span>Загружаем 3D-окно…</span>
                    </div>
                )}>
                    <AdminProduct3DPreview
                        isOpen
                        product={viewer.product}
                        products={viewer.products}
                        categories={viewer.categories}
                        mode={viewer.mode}
                        blankDimensions={viewer.blankDimensions}
                        showBlankFrame={viewer.showBlankFrame}
                        viewerTitle={viewer.viewerTitle}
                        onClose={() => closeProduct3D({ notify: true })}
                        onSelectProduct={selectProduct}
                        onProductUpdated={handleProductUpdated}
                    />
                </Suspense>
            ), document.body) : null}
        </AdminProduct3DViewerContext.Provider>
    );
};

export const useAdminProduct3DViewer = () => {
    const context = useContext(AdminProduct3DViewerContext);

    if (!context) {
        throw new Error('useAdminProduct3DViewer должен использоваться внутри AdminProduct3DViewerProvider');
    }

    return context;
};
